import { Fragment, useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  Clock3,
  FileSpreadsheet,
  Plus,
} from "lucide-react";
import type {
  WeeklyPlan,
  WeeklyPlanExecutionMode,
  WeeklyPlanActivationPreview,
  WeeklyPlanCarryoverCandidate,
  WeeklyPlanningReferenceDataResponse,
} from "../../../shared/weekly-planning";
import { WeeklyPlanningActivationPanel } from "./WeeklyPlanningActivationPanel";
import { WeeklyPlanningExecutionModeField } from "./WeeklyPlanningExecutionModeField";
import { WeeklyPlanningWorkflowActions } from "./WeeklyPlanningWorkflowActions";
import { WeeklyPlanningRowEditor } from "./WeeklyPlanningRowEditor";
import { WeeklyPlanningCarryovers } from "./WeeklyPlanningCarryovers";
import {
  filteredWeeklyPlanRows,
  paginatedWeeklyPlanRows,
  rowIssueLabel,
  rowLineName,
  type WeeklyPlanningRowScope,
} from "./weekly-planning-row-list";

export type WeeklyPlanningViewProps = {
  plan: WeeklyPlan | null;
  lines: WeeklyPlanningReferenceDataResponse["lines"];
  carryovers: WeeklyPlanCarryoverCandidate[];
  selectedCarryoverIds: string[];
  weekStart: string;
  canEdit: boolean;
  canActivate: boolean;
  loading: boolean;
  saving: boolean;
  activating: boolean;
  importing: boolean;
  verifyingRowId: string | null;
  error: string | null;
  notice: string | null;
  dirty: boolean;
  activationConfirmationOpen: boolean;
  activationPreview: WeeklyPlanActivationPreview | null;
  excludedRowsAcknowledged: boolean;
  onWeekStartChange: (value: string) => void;
  onCreate: () => void;
  onImportFile: (file: File) => void;
  onTitleChange: (value: string) => void;
  onNoteChange: (value: string) => void;
  onDayStartsAtChange: (value: "00:00" | "08:00") => void;
  onExecutionModeChange: (value: WeeklyPlanExecutionMode) => void;
  onAddProduction: () => void;
  onAddActivity: () => void;
  onUpdateRow: (rowId: string, field: string, value: string) => void;
  onRemoveRow: (rowId: string) => void;
  onMoveRow: (rowId: string, direction: -1 | 1) => void;
  onToggleCarryover: (
    candidate: WeeklyPlanCarryoverCandidate,
    selected: boolean,
  ) => void;
  onSave: () => void;
  onDiscard: () => void;
  onOpenActivation: () => void;
  onVerifyWorkOrder: (rowId: string, identity: {
    salesOrderNo: string | null;
    customerCode: string | null;
    correctionReason: string | null;
  }) => void;
  onCancelActivation: () => void;
  onActivate: () => void;
  onExcludedRowsAcknowledgedChange: (checked: boolean) => void;
  onRetry: () => void;
};

const number = new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 2 });
const dateTime = new Intl.DateTimeFormat("tr-TR", {
  day: "2-digit",
  month: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  timeZone: "Europe/Istanbul",
});
function dateLabel(value: string) { return value ? new Intl.DateTimeFormat("tr-TR", { day: "2-digit", month: "long", year: "numeric", timeZone: "Europe/Istanbul" }).format(new Date(`${value}T12:00:00`)) : "Plan haftası seçilmedi"; }
export function WeeklyPlanningView(props: WeeklyPlanningViewProps) {
  const { plan } = props;
  const editable = props.canEdit && plan?.status === "DRAFT";
  const busy = props.saving || props.activating || props.importing || Boolean(props.verifyingRowId);
  const inputDisabled = !editable || busy;
  const issueCount =
    plan?.rows.filter((row) => (row.state ?? "READY") !== "READY").length ?? 0;
  const dependencyCount =
    plan?.rows.filter((row) => row.state === "DEPENDENCY_BLOCKED").length ?? 0;
  const incompleteCount = issueCount - dependencyCount;
  const readyCount = (plan?.rows.length ?? 0) - issueCount;
  const operatorSelected = plan?.executionMode === "OPERATOR_SELECTED";
  const pendingProductionCount = plan?.rows.filter((row) => row.kind === "PRODUCTION" && (row.state ?? "READY") !== "READY").length ?? 0;
  const activityCount = plan?.rows.filter((row) => row.kind === "ACTIVITY").length ?? 0;
  const [scope, setScope] = useState<WeeklyPlanningRowScope>(
    issueCount ? "ISSUES" : "ALL",
  );
  const [lineFilter, setLineFilter] = useState("");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [expandedRowId, setExpandedRowId] = useState<string | null>(null);
  useEffect(() => {
    setScope(issueCount ? "ISSUES" : "ALL");
    setLineFilter("");
    setQuery("");
    setPage(1);
    setExpandedRowId(null);
  }, [plan?.id]);
  const lineOptions = useMemo(
    () =>
      Array.from(new Set((plan?.rows ?? []).map(rowLineName))).sort((a, b) =>
        a.localeCompare(b, "tr"),
      ),
    [plan?.rows],
  );
  const filteredRows = useMemo(
    () => filteredWeeklyPlanRows(plan?.rows ?? [], scope, lineFilter, query),
    [plan?.rows, scope, lineFilter, query],
  );
  const pageRows = paginatedWeeklyPlanRows(filteredRows, page);
  const activationReady =
    props.canActivate &&
    !props.dirty &&
    !busy &&
    plan?.status === "DRAFT" &&
    plan.rows.some(
      (row) =>
        row.state === "READY" &&
        row.kind === "PRODUCTION" &&
        (plan.executionMode === "OPERATOR_SELECTED" || (row.scheduledQuantity ?? 0) > 0),
    );
  const totalHours =
    plan?.rows.reduce((sum, row) => sum + (row.plannedHours ?? 0), 0) ?? 0;
  const setFilter = (
    nextScope = scope,
    nextLine = lineFilter,
    nextQuery = query,
  ) => {
    setScope(nextScope);
    setLineFilter(nextLine);
    setQuery(nextQuery);
    setPage(1);
  };
  const importControl = props.canEdit ? (
    <label className="weekly-planning-import">
      <FileSpreadsheet size={16} />
      <span>{props.importing ? "İçe aktarılıyor…" : "Excel'i taslağa al"}</span>
      <input
        type="file"
        accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        disabled={busy}
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) props.onImportFile(file);
          event.currentTarget.value = "";
        }}
      />
    </label>
  ) : null;

  const activation = (
    <WeeklyPlanningActivationPanel
      props={props}
      busy={busy}
      activationReady={activationReady}
    />
  );

  return (
    <section className="weekly-planning" aria-label="Haftalık üretim planı">
      <header className="weekly-planning-heading">
        <div>
          <h2>Haftalık üretim planı</h2>
          <p>
            Excel satırlarını önce eksikleriyle düzeltin, sonra taslağı kaydedip
            karşılaştırın.
          </p>
        </div>
        <div className="weekly-planning-week-control">
          <label htmlFor="weekly-plan-week">Plan haftası</label>
          <input
            id="weekly-plan-week"
            type="date"
            value={props.weekStart}
            disabled={props.loading || busy}
            onChange={(event) => props.onWeekStartChange(event.target.value)}
          />
        </div>
        {plan ? (
          <span
            className={`weekly-planning-status is-${plan.status.toLowerCase()}`}
          >
            {plan.status === "ACTIVE"
              ? "Etkin plan"
              : plan.status === "DRAFT"
                ? "Taslak"
                : "Geçmiş sürüm"}
          </span>
        ) : null}
      </header>
      {props.error ? (
        <div className="weekly-planning-error" role="alert">
          <AlertTriangle size={18} />
          <div>
            <strong>Plan güncellenemedi</strong>
            <p>{props.error}</p>
          </div>
          <button type="button" onClick={props.onRetry}>
            Tekrar dene
          </button>
        </div>
      ) : null}
      {props.notice ? (
        <div className="weekly-planning-notice" role="status">
          <CheckCircle2 size={18} />
          <span>{props.notice}</span>
        </div>
      ) : null}
      {props.loading && !plan ? (
        <div className="weekly-planning-empty" role="status">
          Haftalık plan hazırlanıyor…
        </div>
      ) : !plan ? (
        <div className="weekly-planning-new">
          <div aria-hidden="true" />
          <div>
            <strong>{dateLabel(props.weekStart)} ile başlayan hafta</strong>
            <p>
              Yeni plan önce taslak olarak kaydedilir; üretim kuyruğu değişmez.
            </p>
          </div>
          <div className="weekly-planning-new-actions">
            {importControl}
            <button
              type="button"
              className="primary-button"
              disabled={!props.canEdit || props.loading || busy}
              onClick={props.onCreate}
            >
              <Plus size={17} />
              Boş taslak oluştur
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="weekly-planning-summary" aria-label="Plan özeti">
            <span>
              <small>PLAN HAFTASI</small>
              <strong>{dateLabel(plan.weekStart)}</strong>
            </span>
            <span>
              <small>HAZIR</small>
              <strong>{readyCount} satır</strong>
            </span>
            <span>
              <small>{operatorSelected ? "İNCELEME / ETKİNLİK" : "EKSİK / BAĞLI"}</small>
              <strong>
                {operatorSelected ? `${pendingProductionCount} üretim / ${activityCount} etkinlik` : `${incompleteCount} / ${dependencyCount}`}
              </strong>
            </span>
            <span>
              <small>REVİZYON</small>
              <strong>#{plan.revision}</strong>
            </span>
          </div>
          <div className="weekly-planning-meta">
            <label>
              Plan adı
              <input
                value={plan.title}
                disabled={inputDisabled}
                onChange={(event) => props.onTitleChange(event.target.value)}
              />
            </label>
            <WeeklyPlanningExecutionModeField
              value={plan.executionMode}
              disabled={inputDisabled}
              onChange={props.onExecutionModeChange}
            />
            <label>
              Gün başlangıcı
              <select
                value={plan.dayStartsAt}
                disabled={inputDisabled}
                onChange={(event) =>
                  props.onDayStartsAtChange(
                    event.target.value as "00:00" | "08:00",
                  )
                }
              >
                <option value="08:00">08:00</option>
                <option value="00:00">00:00</option>
              </select>
            </label>
            <label className="weekly-planning-note">
              Plan notu
              <input
                value={plan.note ?? ""}
                disabled={inputDisabled}
                onChange={(event) => props.onNoteChange(event.target.value)}
              />
            </label>
          </div>
          <WeeklyPlanningWorkflowActions
            status={plan.status}
            executionMode={plan.executionMode}
            editable={editable}
            canEdit={props.canEdit}
            busy={busy}
            dirty={props.dirty}
            activationReady={activationReady}
            totalHours={totalHours}
            importControl={importControl}
            onSave={props.onSave}
            onDiscard={props.onDiscard}
            onOpenActivation={props.onOpenActivation}
            onCreate={props.onCreate}
          />
          {activation}
          <div className="weekly-planning-row-actions">
            <div>
              <strong>Plan satırları</strong>
              <span>
                {issueCount
                  ? "Eksik ve bağlı satırlar önce gösteriliyor. Satırı açıp kaynak bilgisini doğrulayarak düzenleyin."
                  : "Tüm satırlar hazır. Arama veya hat filtresiyle düzenleyin."}
              </span>
            </div>
            {editable ? (
              <div>
                <button
                  type="button"
                  onClick={props.onAddProduction}
                  disabled={busy || !props.lines.some((line) => line.active)}
                >
                  <Plus size={16} />
                  Üretim ekle
                </button>
                <button
                  type="button"
                  onClick={props.onAddActivity}
                  disabled={busy || !props.lines.some((line) => line.active)}
                >
                  <Plus size={16} />
                  Etkinlik ekle
                </button>
              </div>
            ) : null}
          </div>
          <section
            className="weekly-planning-filters"
            aria-label="Plan satırı filtreleri"
          >
            <div
              className="weekly-planning-scope"
              role="group"
              aria-label="Satır kapsamı"
            >
              <button
                type="button"
                aria-pressed={scope === "ISSUES"}
                onClick={() => setFilter("ISSUES")}
              >
                {operatorSelected ? "İnceleme ve etkinlikler" : "Eksik ve bağlı"} ({issueCount})
              </button>
              <button
                type="button"
                aria-pressed={scope === "ALL"}
                onClick={() => setFilter("ALL")}
              >
                Tümü ({plan.rows.length})
              </button>
            </div>
            <label>
              Hat
              <select
                value={lineFilter}
                onChange={(event) => setFilter(scope, event.target.value)}
              >
                <option value="">Tüm hatlar</option>
                {lineOptions.map((line) => (
                  <option key={line} value={line}>
                    {line}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Ürün veya iş emri ara
              <input
                type="search"
                value={query}
                onChange={(event) =>
                  setFilter(scope, lineFilter, event.target.value)
                }
                placeholder="Ürün, kod veya iş emri"
              />
            </label>
            <span className="weekly-planning-filter-result">
              {filteredRows.length} satır
            </span>
          </section>
          <div className="weekly-planning-table-wrap">
            <table className="weekly-planning-table">
              <thead>
                <tr>
                  <th scope="col">Sıra</th>
                  <th scope="col">Hat / iş</th>
                  <th scope="col">Durum</th>
                  <th scope="col">Plan</th>
                  {editable ? (
                    <th scope="col">
                      <span className="sr-only">Satır işlemleri</span>
                    </th>
                  ) : null}
                </tr>
              </thead>
              <tbody>
                {pageRows.rows.map((row) => {
                  const expanded = expandedRowId === row.id;
                  const state = row.state ?? "READY";
                  const absoluteIndex = plan.rows.findIndex(
                    (item) => item.id === row.id,
                  );
                  return (
                    <Fragment key={row.id}>
                      <tr className={`is-${state.toLowerCase()}`}>
                        <td className="is-sequence">
                          <strong>{row.sequence}</strong>
                        </td>
                        <td>
                          <strong>{row.productName}</strong>
                          <small>
                            {rowLineName(row)} ·{" "}
                            {row.workOrderNo ?? "İş emri yok"} ·{" "}
                            {row.productCode ?? "Kod yok"}
                          </small>
                        </td>
                        <td>
                          <strong
                            className={`weekly-planning-row-state is-${state.toLowerCase()}`}
                          >
                            {state === "READY"
                              ? "Hazır"
                              : state === "INCOMPLETE"
                                ? "Eksik bilgi"
                                : "Önceki satıra bağlı"}
                          </strong>
                          {state !== "READY" ? (
                            <small>
                              {rowIssueLabel(row) || "Düzenleme gerekli"}
                              {row.source
                                ? ` · Excel ${row.source.sheetName} satır ${row.source.sourceRow}`
                                : ""}
                            </small>
                          ) : null}
                        </td>
                        <td className="weekly-planning-schedule">
                          <Clock3 size={16} />
                          <span>
                            <strong>
                              {props.dirty
                                ? "Taslak değişti"
                                : plan.executionMode === "OPERATOR_SELECTED"
                                  ? state === "READY" ? "Operatör seçimine hazır" : "Mikro doğrulaması bekleniyor"
                                : state === "READY" &&
                                    row.scheduledHours != null
                                  ? `${number.format(row.scheduledHours)} saat`
                                  : "Hesaplanmayı bekliyor"}
                            </strong>
                            <small>
                              {row.kind === "PRODUCTION"
                                ? plan.executionMode === "OPERATOR_SELECTED"
                                  ? `${row.quantity ?? "Miktar boş"} ${row.unit ?? "birim boş"} · saat ve kapasite planlanmaz`
                                  : `${row.quantity ?? "Miktar boş"} ${row.unit ?? "birim boş"} · ${row.capacityPerHour ?? "kapasite boş"}/saat${row.deferredQuantity ? ` · ${number.format(row.deferredQuantity)} ${row.unit ?? ""} devrediyor` : ""}`
                                : `${row.durationHours ?? "Süre boş"} saat`}
                              {!props.dirty &&
                              state === "READY" &&
                              row.scheduledStartAt &&
                              row.scheduledEndAt
                                ? ` · ${dateTime.format(new Date(row.scheduledStartAt))}–${dateTime.format(new Date(row.scheduledEndAt))}`
                                : ""}
                            </small>
                          </span>
                        </td>
                        {editable ? (
                          <td className="weekly-planning-row-controls">
                            <button
                              type="button"
                              aria-label={`${row.productName} satırını düzenle`}
                              aria-expanded={expanded}
                              aria-controls={`weekly-plan-detail-${row.id}`}
                              disabled={busy}
                              onClick={() =>
                                setExpandedRowId(expanded ? null : row.id)
                              }
                            >
                              {expanded ? "Kapat" : "Düzenle"}
                            </button>
                          </td>
                        ) : null}
                      </tr>
                      {expanded ? (
                        <tr
                          id={`weekly-plan-detail-${row.id}`}
                          className="weekly-planning-row-detail"
                        >
                          <td colSpan={editable ? 5 : 4}>
                            <WeeklyPlanningRowEditor
                              row={row}
                              lines={props.lines}
                              inputDisabled={inputDisabled}
                              busy={busy}
                              dirty={props.dirty}
                              verifying={props.verifyingRowId === row.id}
                              operatorSelected={plan.executionMode === "OPERATOR_SELECTED"}
                              index={absoluteIndex}
                              rowCount={plan.rows.length}
                              onUpdate={props.onUpdateRow}
                              onMove={props.onMoveRow}
                              onRemove={props.onRemoveRow}
                              onVerifyWorkOrder={props.onVerifyWorkOrder}
                            />
                          </td>
                        </tr>
                      ) : null}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
            {!filteredRows.length ? (
              <p className="weekly-planning-empty">
                Bu filtrede satır bulunmuyor.{" "}
                {scope === "ISSUES" ? (
                  <button type="button" onClick={() => setFilter("ALL")}>
                    Tüm satırları göster
                  </button>
                ) : null}
              </p>
            ) : null}
          </div>
          {filteredRows.length > 25 ? (
            <nav
              className="weekly-planning-pagination"
              aria-label="Plan satırı sayfaları"
            >
              <button
                type="button"
                disabled={pageRows.currentPage === 1}
                onClick={() => setPage(pageRows.currentPage - 1)}
              >
                Önceki
              </button>
              <span>
                Sayfa {pageRows.currentPage} / {pageRows.totalPages} · sayfada
                en çok 25 satır
              </span>
              <button
                type="button"
                disabled={pageRows.currentPage === pageRows.totalPages}
                onClick={() => setPage(pageRows.currentPage + 1)}
              >
                Sonraki
              </button>
            </nav>
          ) : null}
          {editable ? (
            <WeeklyPlanningCarryovers
              plan={plan}
              carryovers={props.carryovers}
              selectedIds={props.selectedCarryoverIds}
              busy={busy}
              onToggle={props.onToggleCarryover}
            />
          ) : null}
        </>
      )}
    </section>
  );
}
